/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-06
 */

/**
 * Core validation functions for form inputs.
 * These utilities provide consistent validation across all forms in tools.dance.
 */

/**
 * Validates an email address format.
 * @param email - The email address to validate
 * @returns Error message if invalid, undefined if valid
 * @example
 * const error = validateEmail('user@example.com'); // undefined
 * const error = validateEmail('invalid-email'); // 'Please enter a valid email address'
 */
export const validateEmail = (email: string): string | undefined => {
  if (!email) {
    return 'Email is required';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }

  return undefined;
};

/**
 * Validates a password meets security requirements.
 * @param password - The password to validate
 * @returns Error message if invalid, undefined if valid
 * @example
 * const error = validatePassword('StrongPass123'); // undefined
 * const error = validatePassword('weak'); // 'Password must be at least 8 characters long'
 */
export const validatePassword = (password: string): string | undefined => {
  if (!password) {
    return 'Password is required';
  }

  if (password.length < 8) {
    return 'Password must be at least 8 characters long';
  }

  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }

  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }

  if (!/\d/.test(password)) {
    return 'Password must contain at least one number';
  }

  return undefined;
};

/**
 * Validates a name field (first name, last name, etc.).
 * @param name - The name to validate
 * @param fieldName - The display name of the field for error messages
 * @returns Error message if invalid, undefined if valid
 * @example
 * const error = validateName('John', 'First name'); // undefined
 * const error = validateName('', 'Last name'); // 'Last name is required'
 */
export const validateName = (
  name: string,
  fieldName: string
): string | undefined => {
  if (!name || !name.trim()) {
    return `${fieldName} is required`;
  }

  return undefined;
};

/**
 * Validates a phone number format (optional field).
 * @param phone - The phone number to validate
 * @returns Error message if invalid, undefined if valid or empty
 * @example
 * const error = validatePhone('+1-555-123-4567'); // undefined
 * const error = validatePhone('invalid'); // 'Please enter a valid phone number'
 * const error = validatePhone(''); // undefined (optional field)
 */
export const validatePhone = (phone: string): string | undefined => {
  // Phone is optional, so empty is valid
  if (!phone || !phone.trim()) {
    return undefined;
  }

  const phoneRegex = /^\+?[\d\s-()]+$/;
  if (!phoneRegex.test(phone)) {
    return 'Please enter a valid phone number';
  }

  return undefined;
};

/**
 * Validates that password and confirm password fields match.
 * @param password - The original password
 * @param confirmPassword - The password confirmation
 * @returns Error message if passwords don't match, undefined if they match
 * @example
 * const error = validatePasswordMatch('password123', 'password123'); // undefined
 * const error = validatePasswordMatch('password123', 'different'); // 'Passwords do not match'
 */
export const validatePasswordMatch = (
  password: string,
  confirmPassword: string
): string | undefined => {
  if (!confirmPassword) {
    return 'Please confirm your password';
  }

  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }

  return undefined;
};

/**
 * Validates that a required field has a value.
 * @param value - The value to validate
 * @param fieldName - The display name of the field for error messages
 * @returns Error message if empty, undefined if has value
 * @example
 * const error = validateRequired('some value', 'Email'); // undefined
 * const error = validateRequired('', 'Email'); // 'Email is required'
 */
export const validateRequired = (
  value: string,
  fieldName: string
): string | undefined => {
  if (!value || !value.trim()) {
    return `${fieldName} is required`;
  }

  return undefined;
};

/**
 * Validates that a required checkbox is checked.
 * @param checked - Whether the checkbox is checked
 * @param message - The error message to display if not checked
 * @returns Error message if not checked, undefined if checked
 * @example
 * const error = validateCheckbox(true, 'You must accept the terms'); // undefined
 * const error = validateCheckbox(false, 'You must accept the terms'); // 'You must accept the terms'
 */
export const validateCheckbox = (
  checked: boolean,
  message: string
): string | undefined => {
  if (!checked) {
    return message;
  }

  return undefined;
};

/**
 * Validates a URL format (optional field).
 * @param url - The URL to validate
 * @returns Error message if invalid, undefined if valid or empty
 * @example
 * const error = validateUrl('https://example.com'); // undefined
 * const error = validateUrl('invalid-url'); // 'Please enter a valid URL'
 * const error = validateUrl(''); // undefined (optional field)
 */
export const validateUrl = (url: string): string | undefined => {
  // URL is optional, so empty is valid
  if (!url || !url.trim()) {
    return undefined;
  }

  try {
    const urlObject = new URL(url);
    // Ensure it's a valid URL object
    return urlObject
      ? undefined
      : 'Please enter a valid URL (e.g., https://example.com)';
  } catch {
    return 'Please enter a valid URL (e.g., https://example.com)';
  }
};

/**
 * Validates a bio text field (optional field with length limit).
 * @param bio - The bio text to validate
 * @returns Error message if invalid, undefined if valid or empty
 * @example
 * const error = validateBio('Short bio'); // undefined
 * const error = validateBio(''); // undefined (optional field)
 */
export const validateBio = (bio: string): string | undefined => {
  // Bio is optional, so empty is valid
  if (!bio || !bio.trim()) {
    return undefined;
  }

  if (bio.length > 1000) {
    return 'Bio must be 1000 characters or less';
  }

  return undefined;
};

/**
 * Validates a title field (optional field).
 * @param title - The title to validate
 * @returns Error message if invalid, undefined if valid or empty
 * @example
 * const error = validateTitle('Dance Instructor'); // undefined
 * const error = validateTitle(''); // undefined (optional field)
 */
export const validateTitle = (title: string): string | undefined => {
  // Title is optional, so empty is valid
  if (!title || !title.trim()) {
    return undefined;
  }

  if (title.length > 100) {
    return 'Title must be 100 characters or less';
  }

  return undefined;
};

/**
 * Validates a company field (optional field).
 * @param company - The company name to validate
 * @returns Error message if invalid, undefined if valid or empty
 * @example
 * const error = validateCompany('Elite Dance Studio'); // undefined
 * const error = validateCompany(''); // undefined (optional field)
 */
export const validateCompany = (company: string): string | undefined => {
  // Company is optional, so empty is valid
  if (!company || !company.trim()) {
    return undefined;
  }

  if (company.length > 100) {
    return 'Company name must be 100 characters or less';
  }

  return undefined;
};

/**
 * Type definition for validation function.
 */
export type ValidationFunction = (
  value: any,
  ...args: any[]
) => string | undefined;

/**
 * Type definition for validation rules object.
 */
export interface ValidationRules {
  [fieldName: string]: ValidationFunction[];
}

/**
 * Validates address fields with all-or-nothing logic.
 * If any address field has data, then street_address_1, city, and country are required.
 * @param formData - The complete form data object
 * @returns Object with address field errors, or empty object if valid
 * @example
 * const errors = validateAddress(formData);
 * if (errors.streetAddress1) console.log('Street address required');
 */
export const validateAddress = (formData: any): ValidationErrors => {
  const errors: ValidationErrors = {};

  // Check if any address field has data
  const hasAnyAddressData =
    formData.streetAddress1?.trim() ||
    formData.streetAddress2?.trim() ||
    formData.city?.trim() ||
    formData.stateProvince?.trim() ||
    formData.postalCode?.trim() ||
    formData.country?.trim();

  // If any address field has data, require the essential fields
  if (hasAnyAddressData) {
    if (!formData.streetAddress1?.trim()) {
      errors.streetAddress1 =
        'Street address is required when providing an address';
    }
    if (!formData.city?.trim()) {
      errors.city = 'City is required when providing an address';
    }
    if (!formData.country?.trim()) {
      errors.country = 'Country is required when providing an address';
    }
  }

  return errors;
};

/**
 * Type definition for validation errors object.
 */
export interface ValidationErrors {
  [fieldName: string]: string;
}
